import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Link as MuiLink,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLanguageUrl } from '../utils/languageUtils';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentYear = new Date().getFullYear();
  const getLanguageUrl = useLanguageUrl();

  return (
    <Box 
      component="footer"
      role="contentinfo"
      sx={{ 
        textAlign: 'center', 
        mt: 'auto',
        py: 4,
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        boxShadow: '0 -2px 4px -1px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={isMobile ? 1 : 3}
        justifyContent="center"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <MuiLink
          component={Link}
          to={getLanguageUrl('howItWorks')}
          variant="body2"
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
            '&:focus': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
          }}
          aria-label={t('nav.howItWorks')}
        >
          {t('nav.howItWorks')}
        </MuiLink>
        
        <MuiLink
          component={Link}
          to={getLanguageUrl('faq')}
          variant="body2"
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
            '&:focus': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
          }}
          aria-label={t('nav.faq')}
        >
          {t('nav.faq')}
        </MuiLink>
        
        <MuiLink
          component={Link}
          to={getLanguageUrl('whyMonitoringMatters')}
          variant="body2"
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
            '&:focus': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
          }}
          aria-label={t('nav.whyMonitoring')}
        >
          {t('nav.whyMonitoring')}
        </MuiLink>
        
        <MuiLink
          component={Link}
          to={getLanguageUrl('privacyPolicy')}
          variant="body2"
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
            '&:focus': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
          }}
          aria-label={t('footer.privacyPolicy')}
        >
          {t('footer.privacyPolicy')}
        </MuiLink>
        
        <MuiLink
          component={Link}
          to={getLanguageUrl('legalDisclaimer')}
          variant="body2"
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
            '&:focus': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
          }}
          aria-label={t('footer.legalDisclaimer')}
        >
          {t('footer.legalDisclaimer')}
        </MuiLink>
        
        <MuiLink
          component={Link}
          to={getLanguageUrl('ourCommitment')}
          variant="body2"
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
            '&:focus': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
          }}
          aria-label={t('footer.ourCommitment')}
        >
          {t('footer.ourCommitment')}
        </MuiLink>
        
        <MuiLink
          href="mailto:support@alertalegal.co"
          variant="body2"
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
              color: 'primary.main',
            },
            '&:focus': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '2px',
            },
          }}
          aria-label={t('footer.contact')}
        >
          {t('footer.contact')}
        </MuiLink>
      </Stack>
      
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontSize: '0.875rem',
        }}
      >
        {t('footer.copyright', { year: currentYear })}
      </Typography>
    </Box>
  );
};

export default Footer;